import { useState, useEffect } from "react";

interface VideoPlayerProps {
  sampleMovieURL?: {
    size_476_306?: string;
    size_560_360?: string;
    size_644_414?: string;
    size_720_480?: string;
    pc_flag?: number;
    sp_flag?: number;
  };
  title: string;
  posterImage?: string;
}

export function VideoPlayer({ sampleMovieURL, title, posterImage }: VideoPlayerProps) {
  const [selectedSize, setSelectedSize] = useState<string>("");

  // sampleMovieURLがない、または全てのURLが空文字の場合は非表示
  if (!sampleMovieURL) return null;
  
  const hasValidUrl = Object.entries(sampleMovieURL)
    .filter(([key]) => key.startsWith('size_'))
    .some(([_, value]) => value && value !== '');
    
  if (!hasValidUrl) return null;

  const availableSizes = [
    { key: "size_720_480", label: "720x480", width: 720, height: 480 },
    { key: "size_644_414", label: "644x414", width: 644, height: 414 },
    { key: "size_560_360", label: "560x360", width: 560, height: 360 },
    { key: "size_476_306", label: "476x306", width: 476, height: 306 },
  ].filter(size => sampleMovieURL[size.key as keyof typeof sampleMovieURL]);

  // 画面サイズに基づいて理想的なサイズを取得する関数
  const getIdealSizeForViewport = () => {
    const viewportWidth = window.innerWidth;
    
    if (viewportWidth >= 1200) return "size_720_480";
    if (viewportWidth >= 900) return "size_644_414";
    if (viewportWidth >= 600) return "size_560_360";
    return "size_476_306";
  };

  // 利用可能なサイズから最適なものを取得する関数
  const getBestAvailableSize = (idealSize: string) => {
    const availableSizeKeys = availableSizes.map(s => s.key);
    
    // 理想的なサイズが利用可能な場合はそれを返す
    if (availableSizeKeys.includes(idealSize)) {
      return idealSize;
    }
    
    // そうでない場合は優先順位に基づいて選択
    const sizePriority = ["size_720_480", "size_644_414", "size_560_360", "size_476_306"];
    for (const size of sizePriority) {
      if (availableSizeKeys.includes(size)) {
        return size;
      }
    }
    
    // どれも利用できない場合は利用可能なサイズの最初のものを返す
    return availableSizeKeys.length > 0 ? availableSizeKeys[0] : "";
  };

  // コンポーネントマウント時に最適なサイズを選択して動画を表示
  useEffect(() => {
    if (availableSizes.length > 0) {
      const idealSize = getIdealSizeForViewport();
      const bestSize = getBestAvailableSize(idealSize);
      
      if (bestSize) {
        setSelectedSize(bestSize);
      }
    }
  }, []);


  const selectedSizeInfo = availableSizes.find(s => s.key === selectedSize);
  const videoUrl = selectedSize ? String(sampleMovieURL[selectedSize as keyof typeof sampleMovieURL] || "") : "";

  return (
    <div className="mb-6">
      <div className="relative">
        <div className="mb-2">
          <h3 className="text-sm font-medium">動画サイズ: {selectedSizeInfo?.label}</h3>
        </div>
        {videoUrl && selectedSizeInfo && (
          <div 
            className="relative mx-auto overflow-hidden" 
            style={{ 
              maxWidth: `${selectedSizeInfo.width}px`,
              aspectRatio: `${selectedSizeInfo.width} / ${selectedSizeInfo.height}`,
              marginBottom: "2rem" // カルーセルとの間隔を確保
            }}
          >
            {/* 標準的なiframe方式 */}
            <iframe
              src={videoUrl}
              width={selectedSizeInfo.width}
              height={selectedSizeInfo.height}
              allowFullScreen
              className="w-full h-full"
              style={{ display: "block" }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
